import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  FormControl,
  FormHelperText,
  InputBaseComponentProps,
  InputLabel,
  OutlinedInput,
  SxProps,
  useTheme,
  Box,
  Typography,
  Popper,
  Fade,
  Paper,
  IconButton,
  alpha,
  Tooltip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { INTEGER_REGEXP, NUMBER_REGEXP } from '../../constants/regEx';

export interface TextFieldProps {
  id?: string;
  placeholder?: string;
  label?: string;
  endAdornment?: React.ReactNode;
  startAdornment?: React.ReactNode;
  className?: string;
  inputClassName?: string;
  type?: 'number' | 'integer' | 'string' | 'password' | 'text';
  style?: React.CSSProperties;
  disabled?: boolean;
  value?: string | number;
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  onBlur?: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  onPaste?: React.ClipboardEventHandler<HTMLDivElement>;
  onClick?: React.MouseEventHandler<HTMLDivElement> | undefined;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  error?: string;
  name?: string;
  multiline?: boolean;
  required?: boolean;
  number?: boolean;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  inputRef?: React.Ref<any>;
  sx?: SxProps;
  rows?: number;
  showInputProps?: boolean;
  maxLength?: number;
  helperText?: string;
  showCharCount?: boolean;
  maxDecimals?: number;
  validationState?: {
    loading?: boolean;
    valid?: boolean | null;
    suggestion?: string | null;
    suggestions?: string[];
    error?: string | null;
  };
  onApplySuggestion?: (suggestion: string) => void;
}

function SbTextField(props: TextFieldProps) {
  const { type, showInputProps = true, maxLength, value = '', showCharCount } = props;
  const [charCount, setCharCount] = useState<number | undefined>(value ? String(value).length : undefined);
  const theme = useTheme();

  const [isBadgeHovered, setIsBadgeHovered] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const lastStateSigRef = useRef<string>('');

  const loading = props.validationState?.loading;
  const valid = props.validationState?.valid;

  // Reset dismissal whenever a new validation result arrives or validation state changes
  useEffect(() => {
    const currentStateSig = JSON.stringify({
      loading: props.validationState?.loading,
      valid: props.validationState?.valid,
      suggestion: props.validationState?.suggestion,
      suggestions: props.validationState?.suggestions,
      error: props.validationState?.error,
    });

    if (currentStateSig !== lastStateSigRef.current) {
      lastStateSigRef.current = currentStateSig;
      setIsDismissed(false);
    }
  }, [
    props.validationState?.loading,
    props.validationState?.valid,
    props.validationState?.suggestion,
    props.validationState?.suggestions,
    props.validationState?.error,
  ]);

  useEffect(() => {
    if (value) {
      setCharCount(String(value).length);
    } else {
      setCharCount(undefined);
    }
  }, [value]);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (props.onChange) {
      if (type === 'number' || type === 'integer') {
        let regExp = type === 'number' ? NUMBER_REGEXP : INTEGER_REGEXP;
        if (type === 'number' && props.maxDecimals !== undefined) {
          regExp = new RegExp(`^[0-9]{0,10}(?:\\.[0-9]{1,${props.maxDecimals}})?$`);
        }
        if (regExp.test(event.target.value) || event.target.value === '') {
          props.onChange(event);
          setCharCount(value.length);
        }
      } else {
        props.onChange(event);
        setCharCount(value.length);
      }
    }
  };

  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    if (type === 'number' || type === 'integer') {
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (props.onFocus) {
      props.onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  const stepVal =
    props.maxDecimals !== undefined ? (1 / Math.pow(10, props.maxDecimals)).toFixed(props.maxDecimals) : '0.01';
  const inputProps: InputBaseComponentProps = {
    ...(showInputProps && type === 'number' ? { type: 'number', step: stepVal, min: '1' } : {}),
    ...(maxLength ? { maxLength: maxLength } : {}),
  };

  const hasFrontendError = !!props.error;
  const hasAiError = !!props.validationState?.error;
  const hasError = hasFrontendError || hasAiError;

  const isInvalid = valid === false && (!!props.validationState?.suggestion || hasAiError);

  // Popover only opens when invalid (valid === false), unless dismissed.
  // If dismissed, hovering the AI badge chip reactivates it.
  const isPopoverOpen = isInvalid && (!isDismissed || isBadgeHovered);

  const borderStyle = (() => {
    if (loading) {
      return {
        border: `1px solid ${theme.palette.primary.main}`,
        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.12)}`,
      };
    }
    if (valid === false && props.validationState?.suggestion) {
      return {
        border: '1px solid #F87171',
        boxShadow: 'none',
      };
    }
    return {
      border: hasError ? '1px solid #F87171' : `1px solid ${theme.palette.divider}`,
    };
  })();

  const paperStyle = useMemo(() => {
    return {
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
      border: 'none',
      background: theme.palette.mode === 'dark' ? '#261616' : '#FEF2F2',
      borderRadius: '12px',
    };
  }, [theme.palette.mode]);

  const tooltipInnerContent = useMemo(() => {
    if (valid !== false) {
      return null;
    }

    const suggestedValue = props.validationState?.suggestion;
    const rawSuggestions = props.validationState?.suggestions;
    const rawError = props.validationState?.error;

    const suggestionsList =
      rawSuggestions && rawSuggestions.length > 0
        ? rawSuggestions
        : rawError
        ? rawError.split('\n').filter(Boolean)
        : [];

    let mainMessage = '';
    if (suggestedValue) {
      mainMessage = rawError || `This ${props.label || 'value'} doesn't match — did you mean ${suggestedValue}?`;
    } else if (suggestionsList.length === 1) {
      mainMessage = suggestionsList[0];
    } else if (suggestionsList.length > 1) {
      mainMessage = rawError || 'Please review the details below:';
    } else if (rawError) {
      mainMessage = rawError;
    } else {
      mainMessage = `This ${props.label ? props.label.toLowerCase() : 'field'} value needs verification.`;
    }

    return (
      <Box sx={{ position: 'relative', width: '100%' }}>
        {/* Top-Right Dismiss Button */}
        <IconButton
          size="small"
          aria-label="Dismiss AI validation"
          onClick={e => {
            e.stopPropagation();
            setIsBadgeHovered(false);
            setIsDismissed(true);
          }}
          sx={{
            position: 'absolute',
            top: -4,
            right: -4,
            p: '4px',
            color: theme.palette.mode === 'dark' ? '#9CA3AF' : '#6B7280',
            opacity: 0.7,
            '&:hover': {
              opacity: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
            },
          }}
        >
          <CloseIcon sx={{ fontSize: '0.85rem' }} />
        </IconButton>

        {/* Content layout: Left Alert Icon + Message + AI Sparkle */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, pr: 2.5 }}>
          <ErrorOutlineIcon
            sx={{
              fontSize: '1.15rem',
              color: '#EF4444',
              mt: '1px',
              flexShrink: 0,
            }}
          />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Header / Subtitle with subtle sparkle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', mb: 0.25 }}>
              <Typography
                sx={{
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  color: theme.palette.mode === 'dark' ? '#F87171' : '#B91C1C',
                  letterSpacing: '0.1px',
                }}
              >
                Validation Alert
              </Typography>
              <AutoAwesomeIcon
                sx={{
                  fontSize: '0.78rem',
                  color: '#EF4444',
                  opacity: 0.9,
                }}
              />
            </Box>

            {/* Combined Concise Error Text */}
            <Typography
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: theme.palette.mode === 'dark' ? '#E5E7EB' : '#1F2937',
                lineHeight: 1.45,
              }}
            >
              {mainMessage}
            </Typography>

            {/* Multiple suggestions/bullets if present */}
            {suggestionsList.length > 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.75 }}>
                {suggestionsList.map((item, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                    <Typography sx={{ fontSize: '0.75rem', color: '#EF4444', lineHeight: 1.4 }}>•</Typography>
                    <Typography
                      sx={{
                        fontSize: '0.78rem',
                        color: theme.palette.mode === 'dark' ? '#D1D5DB' : '#4B5563',
                        lineHeight: 1.4,
                      }}
                    >
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Suggested action button if suggestion available */}
            {suggestedValue && props.onApplySuggestion && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <button
                  type="button"
                  aria-label={`Use ${suggestedValue}`}
                  onClick={() => {
                    if (props.onApplySuggestion) {
                      props.onApplySuggestion(suggestedValue);
                    }
                  }}
                  style={{
                    backgroundColor: '#DC2626',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '4px 12px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.filter = 'brightness(1.1)';
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.filter = 'none';
                  }}
                >
                  Use {suggestedValue}
                </button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  }, [
    valid,
    props.validationState?.suggestion,
    props.validationState?.suggestions,
    props.validationState?.error,
    props.onApplySuggestion,
    props.label,
    theme.palette.mode,
  ]);

  return (
    <div
      id={props.id}
      ref={containerRef}
      style={props.style || {}}
      className={`sb-textfield-wrapper ${props.className || ''}`}
    >
      <style>{`
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes fadeInSlideUp {
          from {
            opacity: 0;
            transform: translateY(4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes spinPulse {
          0% { transform: rotate(0deg) scale(0.95); opacity: 0.7; }
          50% { transform: rotate(180deg) scale(1.1); opacity: 1; }
          100% { transform: rotate(360deg) scale(0.95); opacity: 0.7; }
        }
        @media (prefers-reduced-motion: reduce) {
          .sb-textfield-input, .MuiOutlinedInput-root, * {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>

      {props.label && (
        <InputLabel
          className="sb-textfield-label"
          required={props.required}
          sx={{
            fontWeight: 500,
            fontSize: '14px',
            color: hasError ? '#EF4444' : theme.palette.common.black,
            marginBottom: '8px',
            display: 'block',
            letterSpacing: '0.3px',
            textTransform: 'none',
            transition: 'color 0.2s ease',
            '&.Mui-focused': {
              color: hasError ? '#EF4444' : theme.palette.common.black,
            },
            '&.Mui-required .MuiFormLabel-asterisk': {
              color: 'inherit',
              marginLeft: '4px',
            },
          }}
        >
          {props.label}
        </InputLabel>
      )}
      <FormControl className={`sb-textfield-control ${props.inputClassName || ''}`} fullWidth error={hasError}>
        <OutlinedInput
          multiline={props.multiline}
          rows={props.rows}
          error={hasError}
          name={props.name}
          type={props.type === 'number' ? 'string' : props.type}
          className="sb-textfield-input"
          placeholder={props.placeholder || ''}
          endAdornment={
            <React.Fragment>
              {loading && (
                <Tooltip title="AI validating..." arrow placement="top">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: theme.palette.primary.main,
                      mr: 1,
                      flexShrink: 0,
                      animation: 'spinPulse 1.5s ease-in-out infinite',
                    }}
                  >
                    <AutoAwesomeIcon sx={{ fontSize: '1rem' }} />
                  </Box>
                </Tooltip>
              )}
              {/* Only show AI Indicator if validation has run and failed (valid === false) */}
              {!loading && valid === false && (props.validationState?.suggestion || hasAiError) && (
                <Tooltip title="AI validated • Click or hover to view suggestion" arrow placement="top">
                  <Box
                    onMouseEnter={() => setIsBadgeHovered(true)}
                    onMouseLeave={() => setIsBadgeHovered(false)}
                    onClick={() => setIsBadgeHovered(prev => !prev)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#EF4444',
                      mr: 0.75,
                      p: '4px',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(239, 68, 68, 0.08)',
                        transform: 'scale(1.1)',
                      },
                      flexShrink: 0,
                    }}
                  >
                    <AutoAwesomeIcon sx={{ fontSize: '1.05rem', color: '#EF4444' }} />
                  </Box>
                </Tooltip>
              )}
              {props.endAdornment || <></>}
            </React.Fragment>
          }
          startAdornment={props.startAdornment || <></>}
          sx={{
            ...props.sx,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(18, 18, 18, 0.7)' : 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',

            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },

            border: borderStyle.border,
            boxShadow: borderStyle.boxShadow || 'none',

            '& .MuiOutlinedInput-input': {
              fontSize: '0.95rem',
              fontWeight: 400,
              color: theme.palette.text.primary,
              padding: '12px 16px',
              letterSpacing: '0.3px',
              transition: 'all 0.2s ease',

              '&::placeholder': {
                color: theme.palette.text.secondary,
                opacity: 0.6,
              },

              '&:autofill, &:-webkit-autofill': {
                WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.paper} inset !important`,
                WebkitTextFillColor: `${theme.palette.text.primary} !important`,
              },
            },

            '&.Mui-focused': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(18, 18, 18, 0.85)' : 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(12px)',
              border: borderStyle.border,
              boxShadow: loading
                ? borderStyle.boxShadow
                : valid === false && props.validationState?.suggestion
                ? `0 0 0 2px rgba(239, 68, 68, 0.15)`
                : hasError
                ? `0 0 0 2px rgba(239, 68, 68, 0.15)`
                : `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
            },

            '&:hover:not(.Mui-focused):not(.Mui-disabled)': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(18, 18, 18, 0.8)' : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(12px)',
              borderColor: loading
                ? theme.palette.primary.main
                : valid === false && props.validationState?.suggestion
                ? '#F87171'
                : hasError
                ? '#F87171'
                : theme.palette.primary.main,
              border: borderStyle.border,
              boxShadow: borderStyle.boxShadow || 'none',
            },

            '&.Mui-disabled': {
              backgroundColor: theme.palette.action.disabledBackground,
              backdropFilter: 'blur(8px)',
              borderColor: theme.palette.divider,
              cursor: 'not-allowed',

              '& .MuiOutlinedInput-input': {
                color: theme.palette.text.disabled,
                WebkitTextFillColor: theme.palette.text.disabled,
                cursor: 'not-allowed',
              },
            },

            '&.MuiOutlinedInput-multiline': {
              padding: 0,

              '& .MuiOutlinedInput-input': {
                padding: '12px 16px',
              },
            },

            '& .MuiInputAdornment-root': {
              color: theme.palette.text.secondary,
              transition: 'color 0.2s ease',
            },

            '&.Mui-focused .MuiInputAdornment-root': {
              color: theme.palette.primary.main,
            },
          }}
          disabled={!!props.disabled}
          id={props.id ? `${props.id}-input` : undefined}
          value={props.value || ''}
          onChange={onChange}
          inputRef={props.inputRef}
          onFocus={handleFocus}
          onBlur={handleBlur}
          required={props.required}
          onPaste={props.onPaste}
          inputProps={inputProps}
          onClick={props.onClick}
          onKeyDown={props.onKeyDown}
          onWheel={handleWheel}
        />

        {/* Frontend Validation Error Message */}
        {hasFrontendError && (
          <FormHelperText
            error
            sx={{
              margin: '6px 4px 0 4px',
              fontSize: '0.75rem',
              fontWeight: 500,
              color: theme.palette.error.main,
              letterSpacing: '0.2px',
              animation: 'slideInDown 0.2s ease',

              '@keyframes slideInDown': {
                from: {
                  opacity: 0,
                  transform: 'translateY(-4px)',
                },
                to: {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            {typeof props.error === 'string' ? props.error : 'Invalid Data'}
          </FormHelperText>
        )}

        {/* Character Count */}
        {showCharCount && charCount !== undefined && (
          <div
            style={{
              textAlign: 'right',
              marginTop: '6px',
              fontSize: '0.7rem',
              fontWeight: 500,
              color: hasError ? theme.palette.error.main : theme.palette.text.secondary,
              letterSpacing: '0.2px',
              transition: 'color 0.2s ease',
            }}
          >
            {charCount}
            {maxLength ? `/${maxLength}` : ''}
          </div>
        )}
      </FormControl>

      {/* Floating Modern AI Validation Popover */}
      <Popper
        open={isPopoverOpen}
        anchorEl={containerRef.current}
        placement="bottom-start"
        transition
        disablePortal={false}
        modifiers={[
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              altAxis: true,
              padding: 8,
            },
          },
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
        ]}
        style={{ zIndex: 990, width: containerRef.current ? containerRef.current.clientWidth : 320, maxWidth: 380 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={isDismissed ? 0 : { enter: 180, exit: 0 }}>
            <Paper
              sx={{
                mt: 1,
                p: '12px 14px',
                position: 'relative',
                animation: 'fadeInSlideUp 0.18s cubic-bezier(0.16, 1, 0.3, 1)',
                ...paperStyle,
              }}
            >
              {tooltipInnerContent}
            </Paper>
          </Fade>
        )}
      </Popper>
    </div>
  );
}

export default SbTextField;
